<script setup lang="ts">
const display = useDisplay()
const router = useRouter()
const route = useRoute()

const systemStore = useSystemStore()

// 관리자 인증 확인 및 라우터 가드
function checkAdminAuth() {
  if (typeof window !== 'undefined') {
    const isLoggedIn = !!sessionStorage.getItem('adminLogin')
    systemStore.adminLogin = isLoggedIn

    // 로그인하지 않은 상태에서 admin 페이지 접근 시 로그인 페이지로 리다이렉트
    if (!isLoggedIn && route.path.startsWith('/admin') && route.path !== '/admin' && route.path !== '/admin/login') {
      router.replace('/admin')
      return false
    }
    return isLoggedIn
  }
  return false
}

const adminMenuList = ref([
  { title: '대시보드', icon: 'mdi-view-dashboard', value: 'dashboard' },
  { title: '캐러셀 이미지 관리', icon: 'mdi-image-album', value: 'carousel' },
  { title: '광고주 관리', icon: 'mdi-paperclip', value: 'advertiser' },
  { title: '크리에이터 관리', icon: 'mdi-creation', value: 'creator' },
  { title: '캠페인 승인 관리', icon: 'mdi-clipboard-check', value: 'campaign' },
  { title: '카테고리 관리', icon: 'mdi-shape-plus', value: 'category' },
  { title: '채널 소유권 검증', icon: 'mdi-shield-check-outline', value: 'channel-verification' },
  { title: '정산/지급 관리', icon: 'mdi-cash-multiple', value: 'payout-queue' },
  { title: 'AI 추천 로그', icon: 'mdi-robot-outline', value: 'recommendation-logs' },
])

/** ✅ 클릭 시 확실히 라우팅(+ 같은 경로면 소프트 리프레시) */
async function onMenuClick(val: string) {
  // 먼저 인증 확인
  if (!checkAdminAuth()) {
    return
  }

  const path = `/admin/${val}`

  try {
    if (route.path === path) {
      // 같은 경로면 강제 리로드
      await router.replace({ path, query: { _r: Date.now().toString() } })
      await nextTick()
      window.location.reload()
    }
    else {
      // 다른 경로로 이동
      await navigateTo(path)
    }
  }
  catch (error) {
    console.error('라우팅 에러:', error)
    // 에러 발생 시 강제 리로드
    window.location.href = path
  }
  finally {
    // 모바일에선 탭 후 자동으로 닫히도록
    if (!display.smAndUp.value)
      systemStore.drawer = false
  }
}

async function logout() {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem('adminLogin')
    systemStore.adminLogin = false
    systemStore.drawer = false
    router.push('/admin')
  }
}

// 라우터 변경 감지하여 인증 체크
watch(() => route.path, () => {
  if (route.path.startsWith('/admin')) {
    checkAdminAuth()
  }
})

onMounted(() => {
  checkAdminAuth()
  if (systemStore.adminLogin) {
    // 데스크톱에서는 기본적으로 drawer 열기
    if (display.smAndUp.value) {
      systemStore.drawer = true
      systemStore.rail = false
    }
    // 모바일에서는 기본적으로 닫기
    else {
      systemStore.drawer = false
    }
  }
})
</script>

<template>
  <v-navigation-drawer
    v-if="systemStore.adminLogin"
    v-model="systemStore.drawer"
    :rail="systemStore.rail"
    :width="systemStore.rail ? 56 : 256"
    color="black"
    :permanent="display.smAndUp.value && systemStore.drawer"
    :temporary="!display.smAndUp.value"
    style="z-index:1005;"
    @update:rail="systemStore.rail = $event"
    @click="systemStore.rail = false"
  >
    <v-list>
      <v-list-item nav height="80">
        <v-list-item-title v-if="!systemStore.rail" class="font-weight-bold text-subtitle-1 pb-1">
          스타킹
        </v-list-item-title>
        <v-list-item-subtitle v-if="!systemStore.rail">
          스타킹 관리자
        </v-list-item-subtitle>

        <template #prepend>
          <v-avatar tile>
            <v-img lazy-src="/logo_yellow.svg" src="/logo_yellow.svg" :cover="false" />
          </v-avatar>
        </template>

        <template #append>
          <v-btn
            v-if="display.smAndUp.value"
            :icon="systemStore.rail ? 'mdi-chevron-right' : 'mdi-chevron-left'"
            variant="text"
            @click.stop="systemStore.rail = !systemStore.rail"
          />
          <v-btn
            v-if="!display.smAndUp.value"
            icon="mdi-close"
            variant="text"
            @click.stop="systemStore.drawer = false"
          />
        </template>
      </v-list-item>
    </v-list>

    <v-divider />

    <v-list density="compact" nav>
      <!-- ✅ :to 제거, 클릭으로 강제 라우팅 -->
      <v-list-item
        v-for="menu in adminMenuList"
        :key="menu.value"
        :prepend-icon="menu.icon"
        :title="menu.title"
        :active="route.path === `/admin/${menu.value}` || route.path.startsWith(`/admin/${menu.value}/`)"
        color="c2"
        link
        rounded="lg"
        role="link"
        tabindex="0"
        @click="onMenuClick(menu.value)"
        @keyup.enter.space="onMenuClick(menu.value)"
      >
        <v-tooltip v-if="systemStore.rail" activator="parent" location="right">
          {{ menu.title }}
        </v-tooltip>
      </v-list-item>
    </v-list>

    <template #append>
      <v-container class="pa-2" fluid>
        <v-row>
          <v-col>
            <v-btn
              block
              color="white"
              variant="tonal"
              :icon="systemStore.rail"
              rounded="lg"
              @click.prevent="logout()"
            >
              <template v-if="systemStore.rail">
                <v-icon icon="mdi-logout" />
              </template>
              <template v-else>
                <v-icon icon="mdi-logout" class="mr-2" />
                <span>로그아웃</span>
              </template>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </v-navigation-drawer>
</template>
